import { useMemo, FC } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { getDate } from 'utils/convertString';
import { getCurrencySymbol } from 'utils/getCurrencySymbol';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { DiscountBadge } from 'components/Core/DiscountBadge';
import { ReactComponent as BrandsIcon } from 'assets/navbar/navbar_brands.svg';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { IProductListData } from 'stores/products/types';
import { productsTableTranslations } from './translations';
import { ProductsTableTransFields } from './types';
import styles from './productsTable.module.scss';
import { useNavigate } from 'react-router';

export const TABLE_LIMIT = 100;

export const ProductsTable: FC = observer(() => {
  const {
    productsStore: { productsList, productsQuery, getProductsList, changeProductStatus },
  } = useStore();

  const { t } = useMyTranslations<ProductsTableTransFields>([productsTableTranslations.name]);
  const navigate = useNavigate();

  const { mutate } = useMutation(getProductsList, {
    mutationKey: 'getProductsList',
  });

  const onEditClick = (item: IProductListData) => {
    navigate(`${item.id}`);
  };

  const columns: Column<IProductListData>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original,
            original: { name, imageUrl, isFromNewspaper },
          },
        }: CellProps<IProductListData>) => {
          return (
            <div className={styles.avatar} onClick={() => onEditClick(original)}>
              {isFromNewspaper && <BrandsIcon className={styles.brandsIcon} />}
              <AvatarIcon avatar={imageUrl} name='imageUrl' size={56} styleProps={{ borderRadius: 8 }} />
              <p className={styles.name}>{name}</p>
            </div>
          );
        },
        accessor: ({ name }) => name,
        width: 8,
        sortType: 'apiSort',
      },
      {
        Header: t('brandName'),
        id: 'brandName',
        Cell: ({
          row: {
            original: {
              brand: { name },
            },
          },
        }: CellProps<IProductListData>) => {
          return <p className={styles.name}>{name}</p>;
        },
        accessor: ({ brand: { name } }) => name,
        width: 4,
        sortType: 'apiSort',
        disableSortBy: true,
      },
      {
        Header: t('price'),
        id: 'price',
        accessor: ({ price, currencyCode }) => `${price.toFixed(2)}${getCurrencySymbol(currencyCode)}`,
        width: 2,
        sortType: 'apiSort',
      },
      {
        Header: t('discountValue'),
        id: 'discountValue',
        Cell: ({
          row: {
            original: { discountType, discountValue, currencyCode, price },
          },
        }: CellProps<IProductListData>) =>
          discountValue && discountType ? (
            <DiscountBadge
              discountValue={discountValue}
              discountType={discountType}
              price={price}
              currencyCode={currencyCode}
            />
          ) : (
            <p>-</p>
          ),
        accessor: ({ discountValue }) => `${discountValue}`,
        width: 2,
        sortType: 'apiSort',
      },
      {
        Header: t('discountPrice'),
        id: 'discountPrice',
        accessor: ({ discountPrice, currencyCode }) =>
          discountPrice ? `${discountPrice.toFixed(2)}${getCurrencySymbol(currencyCode)}` : '-',
        width: 2,
        sortType: 'apiSort',
      },
      {
        Header: t('startDate'),
        id: 'startDate',
        accessor: ({ startDate }) => (startDate ? getDate(startDate) : '-'),
        width: 3,
        sortType: 'apiSort',
      },
      {
        Header: t('expiredDate'),
        id: 'expiredDate',
        accessor: ({ expiredDate }) => (expiredDate ? getDate(expiredDate) : '-'),
        width: 3,
        sortType: 'apiSort',
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IProductListData>) => <StatusCell id={id} status={status} changeStatus={changeProductStatus} />,
        accessor: ({ status }) => status,
        width: 2,
        sortType: 'apiSort',
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IProductListData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => onEditClick(original)}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 2,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    []
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      {productsList && productsList.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...productsList]}
          simpleList
          isPaginated={productsList.length > PAGE_SIZE}
          isManualSorting
          defaultSortId={productsQuery.orderBy}
          defaultSortOrder={productsQuery.orderDirection === 'DESC'}
          apiSort={(orderBy, orderDirection) => {
            if (productsQuery?.orderBy !== orderBy || productsQuery?.orderDirection !== orderDirection) {
              mutate({ orderBy, orderDirection });
            }
          }}
          addData={(offset) => mutate({ offset })}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
});
